import {
  mergeCallbackOptions,
  mergeCallbackOptionsWithPick,
} from "@mirage-web/utils/merge-callbacks";
import { omitKeys } from "@mirage-web/utils/pick-by";
import { DefaultError, useMutation, UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

import { NetworkingLayerConfig } from "../../config";
import { makeDefaultMutationFetcher } from "../../default-fetcher";
import {
  BaseRequestCompletionContext,
  CallerMutationVariables,
  MutationBodySchema,
  MutationDefinitionOptions,
  MutationFetcherOptions,
} from "../types";

type MutationTriggerOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  BodySchema extends MutationBodySchema,
  TransformedResult,
  DelegatedErrorParams,
> = MutationFetcherOptions<
  Path,
  ParamsSchema,
  BodySchema,
  TransformedResult,
  DelegatedErrorParams
> &
  CallerMutationVariables<Path, ParamsSchema, BodySchema>;

interface UseMutationHookCallerOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  BodySchema extends MutationBodySchema,
  TransformedResult,
  DelegatedErrorParams,
>
  extends
    MutationFetcherOptions<Path, ParamsSchema, BodySchema, TransformedResult, DelegatedErrorParams>,
    Omit<
      UseMutationOptions<
        TransformedResult,
        DefaultError,
        CallerMutationVariables<Path, ParamsSchema, BodySchema>,
        unknown
      >,
      "mutationFn" | "onSuccess" | "onError"
    > {
  onSuccess?: (
    result: TransformedResult,
    variables: CallerMutationVariables<Path, ParamsSchema, BodySchema>,
    context: BaseRequestCompletionContext
  ) => Promise<void> | void;
  onError?: (
    error: Error,
    variables: CallerMutationVariables<Path, ParamsSchema, BodySchema>,
    context: BaseRequestCompletionContext
  ) => void;
}

export interface MutationBaseMixin<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  BodySchema extends MutationBodySchema,
  TransformedResult extends object,
  DelegatedErrorParams,
> {
  useMutation(
    callerOptions?: UseMutationHookCallerOptions<
      Path,
      ParamsSchema,
      BodySchema,
      TransformedResult,
      DelegatedErrorParams
    >
  ): ReturnType<
    typeof useMutation<
      TransformedResult,
      DefaultError,
      CallerMutationVariables<Path, ParamsSchema, BodySchema>
    >
  >;

  trigger(
    callerOptions: MutationTriggerOptions<
      Path,
      ParamsSchema,
      BodySchema,
      TransformedResult,
      DelegatedErrorParams
    >
  ): Promise<TransformedResult>;
}

export function createMutationBaseMixin<
  DelegatedErrorParams,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  BodySchema extends MutationBodySchema = z.ZodVoid,
  TransformedResult extends object = z.infer<ResponseSchema>,
>(
  config: NetworkingLayerConfig,
  definitionOptions: MutationDefinitionOptions<
    Path,
    ParamsSchema,
    ResponseSchema,
    BodySchema,
    TransformedResult
  >
): MutationBaseMixin<Path, ParamsSchema, BodySchema, TransformedResult, DelegatedErrorParams> {
  function makeFetcher(
    fetcherOptions: MutationFetcherOptions<
      Path,
      ParamsSchema,
      BodySchema,
      TransformedResult,
      DelegatedErrorParams
    > = {}
  ) {
    const { onError, onSuccess } = mergeCallbackOptionsWithPick(
      ["onError", "onSuccess"],
      definitionOptions,
      fetcherOptions
    );

    return makeDefaultMutationFetcher(config, {
      ...definitionOptions,
      ...fetcherOptions,
      headers: {
        ...definitionOptions.headers,
        ...fetcherOptions.headers,
      },
      getSignal: () => fetcherOptions.signal,
      onError,
      onSuccess,
      delegatedErrorParams: fetcherOptions.delegatedErrorParams,
    });
  }

  return {
    useMutation(
      callerOptions: UseMutationHookCallerOptions<
        Path,
        ParamsSchema,
        BodySchema,
        TransformedResult,
        DelegatedErrorParams
      > = {}
    ) {
      return useMutation({
        mutationFn: makeFetcher(callerOptions),
        ...omitKeys(mergeUseMutationOptions(definitionOptions, callerOptions), [
          "onSuccess",
          "onError",
        ]),
      });
    },
    async trigger(
      callerOptions: MutationTriggerOptions<
        Path,
        ParamsSchema,
        BodySchema,
        TransformedResult,
        DelegatedErrorParams
      >
    ) {
      const fetcher = makeFetcher(callerOptions);

      return fetcher(callerOptions);
    },
  };
}

function mergeUseMutationOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  BodySchema extends MutationBodySchema,
  TransformedResult,
  DelegatedErrorParams,
>(
  ...optionsList: UseMutationHookCallerOptions<
    Path,
    ParamsSchema,
    BodySchema,
    TransformedResult,
    DelegatedErrorParams
  >[]
) {
  return mergeCallbackOptions(["onSettled"], ...optionsList);
}
